'use client'
import React, { useState, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { Download, FileText, X, Search } from 'lucide-react'
import { Form, Input, Button, message, Spin } from 'antd'
import { downloadEbook, getEbookList, getEbookCategoryFilter, getEbookChildrenCategory } from '@/lib/api/resource'
import CommonTaile from '../../Common/CommonTaile'

// 定义实际的资源数据类型
interface EbookItem {
  e_book_id: number
  e_book_name: string
  description_short: string
  e_book_author: string
  cover_image_url: string
  private: boolean
  product_group?: string
  media_type?: string
  file_type?: string
  file_size?: string
  create_date?: string
}

// 分类项类型
interface CategoryItem {
  cls_id: number
  cls_name: string
  level?: number
  children?: CategoryItem[]
  children_check?: number // 是否有子分类：0-无，1-有
  parent_id?: number
  slug?: string
  banner_img_url?: string
  related_count?: number
  isLoading?: boolean // 是否正在加载子分类
}


interface DownloadPageProps {
  locale: string
}


const DownloadPage: React.FC<DownloadPageProps> = ({ locale }) => {
  const t = useTranslations('resource')
  const [form] = Form.useForm()
  
  const breadcrumbItems = [
		{ label: t("Home"), link: "/" },
		{ label: t("Download"), link: "" }
	];

  // 数据状态
  const [ebookList, setEbookList] = useState<EbookItem[]>([])
  const [loading, setLoading] = useState(true)
  const [categoryLoading, setCategoryLoading] = useState(false)

  // 分类数据状态
  const [categories, setCategories] = useState<CategoryItem[]>([])

  // 筛选状态
  const [filters, setFilters] = useState<{
    searchKeyword: string
    selectedCategoryId: number | null
  }>({
    searchKeyword: '',
    selectedCategoryId: null
  })

  // 折叠面板状态
  const [isCategoryPanelExpanded, setIsCategoryPanelExpanded] = useState(true)
  
  // 展开的分类ID集合（用于多级分类展开）
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<number>>(new Set())

  // 模态框状态
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedEbook, setSelectedEbook] = useState<EbookItem | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)

  // 递归更新分类树中的某个分类
  const updateCategoryInTree = (
    categories: CategoryItem[], 
    targetId: number, 
    updater: (cat: CategoryItem) => CategoryItem
  ): CategoryItem[] => {
    return categories.map(cat => {
      if (cat.cls_id === targetId) {
        return updater(cat)
      }
      if (cat.children && cat.children.length > 0) {
        return {
          ...cat,
          children: updateCategoryInTree(cat.children, targetId, updater)
        }
      }
      return cat
    })
  }

  // 获取分类数据 - 初始加载时只获取一级分类，不获取子分类
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoryLoading(true)
        
        // 使用 getEbookCategoryFilter 获取一级分类数据
        const result = await getEbookCategoryFilter({
          lang_code: { lang_code: locale },
          level: 1
        })
        
        // 根据实际返回的数据结构：数据在 detail.cls_list 字段中
        const categoryList = result?.detail?.cls_list || result?.cls_list || []
        
        // 根据实际字段名映射：e_book_category_id 和 e_book_category_name
        // children_check: 0-无子分类，不为0-有子分类
        // 初始时 children 为空数组，点击 + 时才加载
        const firstLevelCategories = Array.isArray(categoryList)
          ? categoryList.map((item: any) => ({
              cls_id: item.e_book_category_id || item.cls_id || item.id || 0,
              cls_name: item.e_book_category_name || item.cls_name || item.name || '',
              level: 1,
              children: [], // 初始为空数组，点击 + 时才加载
              children_check: item.e_book_category_children_check || 0, // 0-无子分类，不为0-有子分类
              parent_id: item.e_book_category_parent || 0,
              slug: item.e_book_category_slug || '',
              banner_img_url: item.banner_img_url || '',
              related_count: item.e_book_category_related_count || 0,
              isLoading: false
            }))
          : []
        
        // 直接设置分类，不获取子分类
        setCategories(firstLevelCategories)
        
        // 默认选中第一个分类
        if (firstLevelCategories.length > 0) {
          const firstCategory = firstLevelCategories[0]
          setFilters(prev => ({
            ...prev,
            selectedCategoryId: firstCategory.cls_id,
            searchKeyword: '' // 清空搜索关键词
          }))
        }
        
      } catch (error) {
        setCategories([])
      } finally {
        setCategoryLoading(false)
      }
    }

    fetchCategories()
  }, [locale])

  // 获取电子书列表 - category_id_list 和 keyword 互斥
  useEffect(() => {
    const fetchEbookList = async () => {
      try {
        setLoading(true)
        
        // 构建请求参数：category_id_list 和 keyword 互斥
        const requestParams: any = {
          page: 1,
          limit: 100,
          lang_code: {
            lang_code: locale
          }
        }
        
        // 如果选中了分类，只传 category_id_list
        if (filters.selectedCategoryId) {
          requestParams.category_id_list = [filters.selectedCategoryId]
        } 
        // 如果有搜索关键词，只传 keyword（且没有选中分类时）
        else if (filters.searchKeyword && filters.searchKeyword.trim()) {
          requestParams.keyword = filters.searchKeyword.trim()
        }
        // 两者都没有时，都不传（获取全部）
        else {
          // console.log('🔄 获取全部电子书')
        }
        
        const result = await getEbookList(requestParams)
        
        const ebookListData = Array.isArray(result?.detail?.ebook_list) 
          ? result.detail.ebook_list 
          : []
        
        setEbookList(ebookListData)
      } catch (error) {
        setEbookList([])
      } finally {
        setLoading(false)
      }
    }

    fetchEbookList()
  }, [locale, filters.selectedCategoryId, filters.searchKeyword])

  // 过滤电子书（只过滤私有电子书，搜索和分类筛选已在接口层面处理）
  const filteredEbooks = useMemo(() => {
    // 过滤掉私有电子书
    const filtered = ebookList.filter(item => !item.private)
    return filtered
  }, [ebookList])

  // 切换分类展开/折叠（用于多级分类）- 点击 + 时按需加载子分类
  const toggleCategoryExpand = async (categoryId: number, category: CategoryItem) => {
    // 如果已经展开，则折叠
    if (expandedCategoryIds.has(categoryId)) {
      setExpandedCategoryIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(categoryId)
        return newSet
      })
      return
    }

    // 如果已经有子分类数据，直接展开
    if (category.children && category.children.length > 0) {
      setExpandedCategoryIds(prev => new Set(prev).add(categoryId))
      return
    }

    // 如果 children_check !== 0（有子分类）但还没加载，则加载子分类
    if (category.children_check !== 0) {
      try {
        // 更新分类的加载状态
        setCategories(prev => updateCategoryInTree(prev, categoryId, (cat) => ({ ...cat, isLoading: true })))

        // 调用子分类查询接口
        const result = await getEbookChildrenCategory({
          parent_cls_id: categoryId,
          lang_code: locale
        })

        const childCategoryList = result?.detail?.cls_list || result?.cls_list || []
        
        // 映射子分类数据
        const validChildCategories = Array.isArray(childCategoryList)
          ? childCategoryList.map((item: any) => ({
              cls_id: item.e_book_category_id || item.cls_id || item.id || 0,
              cls_name: item.e_book_category_name || item.cls_name || item.name || '',
              level: (category.level || 0) + 1,
              children: [], // 子分类的子分类也初始为空，点击 + 时才加载
              children_check: item.e_book_category_children_check || 0, // 0-无子分类，不为0-有子分类
              parent_id: item.e_book_category_parent || categoryId,
              slug: item.e_book_category_slug || '',
              banner_img_url: item.banner_img_url || '',
              related_count: item.e_book_category_related_count || 0,
              isLoading: false
            }))
          : []

        // 更新分类列表，添加子分类
        setCategories(prev => updateCategoryInTree(prev, categoryId, (cat) => ({ 
          ...cat, 
          children: validChildCategories, 
          isLoading: false 
        })))

        // 展开该分类
        setExpandedCategoryIds(prev => new Set(prev).add(categoryId))
      } catch (error) {
        console.error("❌ Error fetching child categories:", error)
        setCategories(prev => updateCategoryInTree(prev, categoryId, (cat) => ({ ...cat, isLoading: false })))
      }
    }
  }


  // 选择分类 - 选择分类时清空搜索关键词（因为互斥）
  const handleCategorySelect = (categoryId: number, event?: React.MouseEvent) => {
    // 阻止事件冒泡，避免触发展开/折叠
    if (event) {
      event.stopPropagation()
    }

    setFilters(prev => {
      const newCategoryId = prev.selectedCategoryId === categoryId ? null : categoryId
      return {
        ...prev,
        selectedCategoryId: newCategoryId,
        // 选择分类时清空搜索关键词（因为互斥）
        searchKeyword: newCategoryId ? '' : prev.searchKeyword
      }
    })
  }

  // 递归渲染分类列表（支持多级）- 参考 ProductConfigPanel 样式
  const renderCategoryList = (categoryList: CategoryItem[], level: number = 0): React.ReactNode => {
    return categoryList.map((item) => {
      // children_check !== 0 表示有子分类，才显示 + 号
      const hasChildren = item.children_check !== 0
      const isExpanded = expandedCategoryIds.has(item.cls_id)
      const isSelected = filters.selectedCategoryId === item.cls_id
      const hasLoadedChildren = item.children && item.children.length > 0

      // 根据层级设置不同的背景色：一级分类 bg-gray-100，二级分类 bg-gray-50（更浅）
      const backgroundColor = level > 0 
        ? (isSelected ? 'bg-mainColor/5' : 'bg-gray-50')
        : (isSelected ? 'bg-mainColor/10' : 'bg-gray-100')

      return (
        <div key={item.cls_id} className={level > 0 ? '' : ''}>
          {/* 分类项头部 - 参考 ProductConfigPanel 样式 */}
          <div
            className={`group flex items-center justify-between cursor-pointer p-3 transition-all duration-200 hover:bg-mainColor/20 ${backgroundColor}`}
          >
            {/* 左侧：展开按钮和分类名称 */}
            <div className="flex items-center flex-1">
              {/* 展开/折叠按钮 - 只有 children_check !== 0 时才显示 */}
              {hasChildren && (
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    toggleCategoryExpand(item.cls_id, item)
                  }}
                  className="flex h-7 w-7 items-center justify-center text-gray-600 hover:text-gray-800 transition-colors mr-2 text-xl font-bold"
                  title={isExpanded ? '折叠' : '展开'}
                  disabled={item.isLoading}
                >
                  {item.isLoading ? (
                    <span className="text-xs animate-spin">⏳</span>
                  ) : isExpanded ? (
                    "−"
                  ) : (
                    "+"
                  )}
                </button>
              )}
              {!hasChildren && <div className="w-7 mr-2" />}
              
              {/* 分类名称按钮 */}
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleCategorySelect(item.cls_id, e)
                }}
                className={`flex-1 text-left text-base font-medium transition-colors ${
                  isSelected
                    ? 'text-mainColor font-semibold'
                    : 'text-gray-800'
                }`}
              >
                {item.cls_name}
              </button>
            </div>
          </div>
          
          {/* 子分类列表 - 只有展开且有已加载的子分类时才显示 */}
          {isExpanded && hasLoadedChildren && (
            <div
              className={`overflow-hidden transition-all duration-500 ease-in-out
                ${isExpanded ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"}`}
              style={{
                transitionProperty: 'max-height, opacity',
                transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <div className="bg-white">
                {renderCategoryList(item.children, level + 1)}
              </div>
            </div>
          )}
        </div>
      )
    })
  }

  // 处理搜索 - 输入搜索关键词时清空分类选择（因为互斥）
  const handleSearch = (value: string) => {
    setFilters(prev => ({
      ...prev,
      searchKeyword: value,
      // 输入搜索关键词时清空分类选择（因为互斥）
      selectedCategoryId: value.trim() ? null : prev.selectedCategoryId
    }))
  }

  // 打开下载模态框
  const handleDownloadClick = (ebook: EbookItem) => {
    setSelectedEbook(ebook)
    setIsModalOpen(true)
    form.resetFields()
  }

  // 关闭模态框
  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedEbook(null)
    form.resetFields()
  }

  // 提交下载请求
  const handleDownloadSubmit = async (values: any) => {
    if (!selectedEbook) return

    setIsDownloading(true)

    try {
      const params = {
        ebook_id: selectedEbook.e_book_id,
        first_name: values.full_name || '', // 使用full_name的值
        last_name: '', // 固定为空字符串
        phone_number: values.phone_number,
        work_email: values.work_email,
        company_name: values.company_name,
        ...(selectedEbook.private && values.password && { password: values.password })
      }

      await downloadEbook(params, locale)

      // 下载成功，关闭模态框
      closeModal()
      message.success(t("DownloadStartedSuccess"))

    } catch (error) {
      console.error('Download failed:', error)
      message.error(t("DownloadFailed"))
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div className="resources-page">
      <CommonTaile msg={t("Download")} breadcrumbItems={breadcrumbItems} backgroundImage="/image/plsyy/download.webp" pageSlug="service/download" />
      
      {/* Main Content Section */}
      <section className="container mx-auto py-8 px-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Sidebar - Filters */}
          <div className="w-full lg:w-80 flex-shrink-0">
            {/* 主卡片容器 - 参考 ProductConfigPanel 样式 */}
            <div className="overflow-hidden border border-gray-200 bg-white shadow-md w-full">
              {/* 标题区域 */}
              <div className="bg-mainColor px-6 py-4">
                <span className="text-xl font-bold text-white flex items-center gap-2">
                  {t("Download")}
                </span>
              </div>

              {/* 搜索框区域 */}
              <div className="px-5 py-4 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder={t("Type-to-search")}
                    value={filters.searchKeyword}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-10 h-10 rounded"
                  />
                </div>
              </div>

              {/* 分类列表区域 */}
              <div className="list-cate">
                <div className="relative">
                  {/* 分类标题和展开/折叠按钮 */}
                  <div
                    className={`group flex items-center justify-between cursor-pointer py-3 px-5 transition-all duration-200`}
                  >
                    <span className="text-base font-medium text-gray-800 flex-1">
                      {t("Category")}
                    </span>
                  </div>

                  {/* 分类列表内容 */}
                  <div
                    className={`overflow-hidden transition-all duration-500 ease-in-out
                      ${isCategoryPanelExpanded ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"}`}
                    style={{
                      transitionProperty: 'max-height, opacity',
                      transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    <div className="bg-white">
                      {categoryLoading ? (
                        <div className="py-8 flex items-center justify-center">
                          <Spin size="large" />
                        </div>
                      ) : categories.length > 0 ? (
                        <div className="space-y-1 max-h-96 overflow-y-auto">
                          {renderCategoryList(categories)}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400 py-4 text-center">{t('No-classified')}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Ebook List */}
          <div className="flex-1 bg-white shadow-sm">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Spin size="large" />
              </div>
            ) : filteredEbooks.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {filteredEbooks.map((item) => (
                  <div
                    key={item.e_book_id}
                    className="flex items-center gap-6 p-5 hover:bg-gray-50 transition-colors"
                  >
                    {/* File Icon */}
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded">
                        <FileText className="w-6 h-6 text-gray-500" />
                      </div>
                    </div>

                    {/* File Info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 mb-2 text-base">
                        {item.e_book_name}
                      </div>
                      {item.description_short && (
                        <div className="text-sm text-gray-600 line-clamp-2">
                          {/* <span className="font-medium">{t("BriefIntroduction")}: </span> */}
                          {item.description_short}
                        </div>
                      )}
                    </div>

                    {/* Download Button */}
                    <div className="flex-shrink-0">
                      <Button
                        type="primary"
                        icon={<Download className="w-4 h-4" />}
                        onClick={() => handleDownloadClick(item)}
                        className="!bg-mainColor !border-mainColor hover:!bg-mainColor/90"
                        size="middle"
                      >
                        {t("Download")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-500 text-lg mb-4">{t("NoEbooksFound")}</div>
                <p className="text-gray-400">{t("NoEbooksDesc")}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Download Modal */}
      {isModalOpen && selectedEbook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl max-w-[600px] w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-800">
                {t("DownloadFormTitle")}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Selected Ebook Info */}
              <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="relative w-16 h-20 bg-gray-200 rounded overflow-hidden">
                  <Image
                    src={selectedEbook.cover_image_url || '/image/default-image2.webp'}
                    alt={selectedEbook.e_book_name}
                    fill
                    className="object-cover rounded"
                    unoptimized
                  />
                  {selectedEbook.private && (
                    <div className="absolute -top-1 -right-1 bg-orange-500 text-white rounded-full p-1 z-10">
                      <span className="text-xs">🔒</span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-800 line-clamp-2">
                    {selectedEbook.e_book_name}
                  </h4>
                  {selectedEbook.e_book_author && (
                    <p className="text-sm text-gray-600">{t("Author")}: {selectedEbook.e_book_author}</p>
                  )}
                  {selectedEbook.private && (
                    <p className="text-xs text-orange-600 mt-1">
                      🔒 {t("PrivateEbookNotice")}
                    </p>
                  )}
                </div>
              </div>

              {/* Download Form */}
              <Form
                form={form}
                layout="vertical"
                onFinish={handleDownloadSubmit}
                className="space-y-4"
              >
                {/* 只显示一个Name字段 */}
                <Form.Item
                  label={<span className="text-gray-700 font-medium">{t("FullName")}</span>}
                  name="full_name"
                  rules={[{ required: true, message: t("NameRequired") }]}
                >
                  <Input
                    className="h-10 rounded-lg"
                    placeholder={t("EnterFullName")}
                  />
                </Form.Item>

                <Form.Item
                  label={<span className="text-gray-700 font-medium">{t("WorkEmail")}</span>}
                  name="work_email"
                  rules={[
                    { required: true, message: t("EmailRequired") },
                    { type: 'email', message: t("EmailInvalid") }
                  ]}
                >
                  <Input
                    className="h-10 rounded-lg"
                    placeholder={t("EnterWorkEmail")}
                  />
                </Form.Item>

                <Form.Item
                  label={<span className="text-gray-700 font-medium">{t("PhoneNumber")}</span>}
                  name="phone_number"
                  rules={[{ required: true, message: t("PhoneRequired") }]}
                >
                  <Input
                    className="h-10 rounded-lg"
                    placeholder={t("EnterPhoneNumber")}
                  />
                </Form.Item>

                <Form.Item
                  label={<span className="text-gray-700 font-medium">{t("CompanyName")}</span>}
                  name="company_name"
                >
                  <Input
                    className="h-10 rounded-lg"
                    placeholder={t("EnterCompanyName")}
                  />
                </Form.Item>

                {/* 密码字段 - 只在private为true时显示 */}
                {selectedEbook?.private && (
                  <Form.Item
                    label={<span className="text-gray-700 font-medium">{t("Password")}</span>}
                    name="password"
                    rules={[{ required: true, message: t("PasswordRequired") }]}
                  >
                    <Input.Password
                      className="h-10 rounded-lg"
                      placeholder={t("EnterPassword")}
                    />
                  </Form.Item>
                )}

                {/* Submit Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={closeModal}
                    className="flex-1 h-10 rounded-md"
                    size="large"
                  >
                    {t("Cancel")}
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isDownloading}
                    className="flex-1 h-10 !rounded-md !shadow-none !bg-mainColor !hover:bg-mainColor !border-mainColor !hover:border-mainColor"
                    size="large"
                    icon={<Download className="w-4 h-4" />}
                  >
                    {isDownloading ? t("Downloading") : t("DownloadButton")}
                  </Button>
                </div>
              </Form>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default DownloadPage